import React, { useEffect, useMemo, useRef, useState } from 'react';
import Matter from 'matter-js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import charmImage from '../images/charm.png';
import powerballBallImage from '../images/powerball-ball.png';

const BOARD_WIDTH = 560;
const BOARD_HEIGHT = 576;
const SLOT_COUNT = 9;
const PEG_ROWS = 9;
const PEG_RADIUS = 5;
const SLOT_WALL_HEIGHT = 44;
const NUMBER_DIVIDER_WIDTH = 1.5;
const DROP_GRAVITY = 0.34;
const DROP_TIMEOUT_MS = 18000;
const EDGE_RAIL_X = 45;
const EDGE_RAIL_BOTTOM = 478;
const EDGE_RAIL_WIDTH = 8;

export const GAME_RULES = {
  powerball: {
    title: 'POWERBALL',
    mainMax: 69,
    specialMax: 26,
    backTo: '/tellmemorepower'
  },
  mega: {
    title: 'Mega Millions',
    mainMax: 70,
    specialMax: 24,
    backTo: '/tellmemoremega'
  }
};

function randomInt(max) {
  if (window.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return (values[0] % max) + 1;
  }
  return Math.floor(Math.random() * max) + 1;
}

function pickUnique(max, excluded = []) {
  const used = new Set(excluded);
  let value = randomInt(max);
  while (used.has(value)) value = randomInt(max);
  return value;
}

export function makeSlotValues(max, excluded = []) {
  const values = [];
  while (values.length < SLOT_COUNT) {
    values.push(pickUnique(max, [...excluded, ...values]));
  }
  return values;
}

export function getDropVisual(game, isSpecialDrop) {
  if (!isSpecialDrop) return 'charm';
  return 'powerball';
}

function createRail(Bodies, start, end, options) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Bodies.rectangle(
    (start.x + end.x) / 2,
    (start.y + end.y) / 2,
    Math.hypot(dx, dy),
    EDGE_RAIL_WIDTH,
    {
      ...options,
      angle: Math.atan2(dy, dx),
      friction: 0,
      restitution: 0.62,
      render: { visible: false }
    }
  );
}

export function createBoardBodies() {
  const { Bodies } = Matter;
  const staticOptions = {
    isStatic: true,
    render: { fillStyle: '#111111' }
  };
  const bodies = [
    Bodies.rectangle(-8, BOARD_HEIGHT / 2, 16, BOARD_HEIGHT, staticOptions),
    Bodies.rectangle(BOARD_WIDTH + 8, BOARD_HEIGHT / 2, 16, BOARD_HEIGHT, staticOptions),
    Bodies.rectangle(BOARD_WIDTH / 2, BOARD_HEIGHT + 8, BOARD_WIDTH, 16, staticOptions),
    createRail(Bodies, { x: EDGE_RAIL_X, y: 0 }, { x: EDGE_RAIL_X, y: EDGE_RAIL_BOTTOM }, staticOptions),
    createRail(
      Bodies,
      { x: BOARD_WIDTH - EDGE_RAIL_X, y: 0 },
      { x: BOARD_WIDTH - EDGE_RAIL_X, y: EDGE_RAIL_BOTTOM },
      staticOptions
    ),
    createRail(
      Bodies,
      { x: EDGE_RAIL_X, y: EDGE_RAIL_BOTTOM },
      { x: 4, y: BOARD_HEIGHT - SLOT_WALL_HEIGHT },
      staticOptions
    ),
    createRail(
      Bodies,
      { x: BOARD_WIDTH - EDGE_RAIL_X, y: EDGE_RAIL_BOTTOM },
      { x: BOARD_WIDTH - 4, y: BOARD_HEIGHT - SLOT_WALL_HEIGHT },
      staticOptions
    )
  ];

  for (let row = 0; row < PEG_ROWS; row += 1) {
    const count = row % 2 === 0 ? 8 : 9;
    for (let column = 0; column < count; column += 1) {
      const x = row % 2 === 0
        ? ((column + 1) * BOARD_WIDTH) / 9
        : ((column + 0.5) * BOARD_WIDTH) / 9;
      bodies.push(Bodies.circle(x, 82 + row * 48, PEG_RADIUS, {
        ...staticOptions,
        restitution: 0.78,
        render: { fillStyle: '#000000' }
      }));
    }
  }

  const slotWidth = BOARD_WIDTH / SLOT_COUNT;
  for (let index = 1; index < SLOT_COUNT; index += 1) {
    bodies.push(Bodies.rectangle(
      index * slotWidth,
      BOARD_HEIGHT - SLOT_WALL_HEIGHT / 2,
      NUMBER_DIVIDER_WIDTH,
      SLOT_WALL_HEIGHT,
      staticOptions
    ));
  }

  return bodies;
}

function LuckyDrop() {
  const location = useLocation();
  const navigate = useNavigate();
  const [game] = useState(() => readDropGame(
    location.state?.game,
    location.state?.dropEntryVersion
  ));
  const rules = GAME_RULES[game];
  const canvasRef = useRef(null);
  const charmVisualRef = useRef(null);
  const physicsRef = useRef(null);
  const activeCharmRef = useRef(null);
  const numbersRef = useRef([]);
  const initialSlotValues = useMemo(
    () => rules ? makeSlotValues(rules.mainMax) : Array(SLOT_COUNT).fill(''),
    [rules]
  );
  const slotValuesRef = useRef(initialSlotValues);
  const [numbers, setNumbers] = useState([]);
  const [slotValues, setSlotValues] = useState(initialSlotValues);
  const [landedSlot, setLandedSlot] = useState(null);
  const [dropping, setDropping] = useState(false);
  const [dropVisual, setDropVisual] = useState('charm');

  const isComplete = numbers.length === 6;
  const status = useMemo(() => {
    if (dropping) return `Charm ${numbers.length + 1} is falling`;
    if (isComplete) return 'Your lucky numbers are ready';
    return `${6 - numbers.length} ${6 - numbers.length === 1 ? 'drop' : 'drops'} left`;
  }, [dropping, isComplete, numbers.length]);

  useEffect(() => {
    const image = new Image();
    image.decoding = 'async';
    image.src = powerballBallImage;
    if (typeof image.decode === 'function') image.decode().catch(() => {});
  }, []);

  useEffect(() => {
    if (!location.state) return;
    navigate('/drop', { replace: true, state: null });
  }, [location.state, navigate]);

  useEffect(() => {
    if (!rules || !canvasRef.current || navigator.userAgent.includes('jsdom')) return undefined;

    const { Body, Bodies, Composite, Engine, Events, Render, Runner } = Matter;
    const charmVisual = charmVisualRef.current;
    const engine = Engine.create({ enableSleeping: false });
    engine.gravity.y = DROP_GRAVITY;
    engine.positionIterations = 8;
    engine.velocityIterations = 6;
    const render = Render.create({
      canvas: canvasRef.current,
      engine,
      options: {
        width: BOARD_WIDTH,
        height: BOARD_HEIGHT,
        wireframes: false,
        background: '#ffffff',
        showSleeping: false,
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2)
      }
    });
    const runner = Runner.create();

    Composite.add(engine.world, createBoardBodies());

    const finishDrop = (charm) => {
      const slotWidth = BOARD_WIDTH / SLOT_COUNT;
      const slot = Math.max(0, Math.min(SLOT_COUNT - 1, Math.floor(charm.position.x / slotWidth)));
      const value = slotValuesRef.current[slot];
      const pick = { value, special: numbersRef.current.length === 5 };

      Composite.remove(engine.world, charm);
      activeCharmRef.current = null;
      if (charmVisual) charmVisual.style.visibility = 'hidden';
      numbersRef.current = [...numbersRef.current, pick];
      setNumbers(numbersRef.current);
      setLandedSlot(slot);
      setDropping(false);
    };

    Events.on(engine, 'afterUpdate', () => {
      const charm = activeCharmRef.current;
      if (!charm) return;

      if (charmVisual) {
        charmVisual.style.left = `${(charm.position.x / BOARD_WIDTH) * 100}%`;
        charmVisual.style.top = `${(charm.position.y / BOARD_HEIGHT) * 100}%`;
        charmVisual.style.transform = `translate(-50%, -50%) rotate(${charm.angle}rad)`;
      }

      const hasSettledInSlot = charm.position.y > BOARD_HEIGHT - 30 && charm.speed < 1.25;
      const hasTimedOut = engine.timing.timestamp - (charm.plugin.droppedAt ?? 0) > DROP_TIMEOUT_MS;
      if (hasSettledInSlot || hasTimedOut) finishDrop(charm);
    });

    Render.run(render);
    Runner.run(runner, engine);
    physicsRef.current = { Body, Bodies, Composite, engine };

    return () => {
      Render.stop(render);
      Runner.stop(runner);
      Composite.clear(engine.world, false);
      Engine.clear(engine);
      physicsRef.current = null;
      activeCharmRef.current = null;
      if (charmVisual) charmVisual.style.visibility = 'hidden';
    };
  }, [rules]);

  function resetGame() {
    const physics = physicsRef.current;
    if (physics && activeCharmRef.current) {
      physics.Composite.remove(physics.engine.world, activeCharmRef.current);
    }
    activeCharmRef.current = null;
    numbersRef.current = [];
    slotValuesRef.current = makeSlotValues(rules.mainMax);
    setNumbers([]);
    setSlotValues(slotValuesRef.current);
    setLandedSlot(null);
    setDropping(false);
    setDropVisual('charm');
    if (charmVisualRef.current) {
      charmVisualRef.current.style.visibility = 'hidden';
      charmVisualRef.current.style.left = '50%';
      charmVisualRef.current.style.top = '0';
      charmVisualRef.current.style.transform = 'translate(-50%, -50%)';
    }
  }

  function handleDrop() {
    if (dropping) return;
    if (isComplete) {
      resetGame();
      return;
    }

    const physics = physicsRef.current;
    if (!physics) return;
    const isSpecial = numbersRef.current.length === 5;
    const max = isSpecial ? rules.specialMax : rules.mainMax;
    const excluded = isSpecial ? [] : numbersRef.current.map((pick) => pick.value);
    const nextSlotValues = makeSlotValues(max, excluded);
    const charm = physics.Bodies.circle(
      BOARD_WIDTH / 2 + randomInt(25) - 13,
      26,
      14,
      {
        label: 'lucky-charm',
        restitution: 0.72,
        friction: 0.002,
        frictionAir: 0.006,
        density: 0.002,
        render: { visible: false },
        plugin: { droppedAt: physics.engine.timing.timestamp }
      }
    );

    slotValuesRef.current = nextSlotValues;
    setSlotValues(nextSlotValues);
    setLandedSlot(null);
    setDropVisual(getDropVisual(game, isSpecial));
    setDropping(true);
    activeCharmRef.current = charm;
    if (charmVisualRef.current) {
      charmVisualRef.current.style.left = '50%';
      charmVisualRef.current.style.top = `${(26 / BOARD_HEIGHT) * 100}%`;
      charmVisualRef.current.style.visibility = 'visible';
    }
    physics.Composite.add(physics.engine.world, charm);
    physics.Body.setVelocity(charm, { x: (randomInt(13) - 7) / 12, y: 0 });
    physics.Body.setAngularVelocity(charm, (randomInt(21) - 11) / 100);
  }

  return (
    <main className={`luckyDropPage luckyDropPage--${game}`}>
      <div className="luckyDropShell">
        <Link className="luckyDropBack" to={rules.backTo}>Back</Link>

        <div className="luckyPickRow" aria-label="Your lucky numbers" aria-live="polite">
          {Array.from({ length: 6 }, (_, index) => {
            const pick = numbers[index];
            return (
              <span
                className={`luckyPickBall ${index === 5 ? 'luckyPickBall--special' : ''} ${pick ? 'luckyPickBall--filled' : ''}`}
                key={index}
              >
                {pick?.value ?? '–'}
              </span>
            );
          })}
        </div>

        <section className="physicsBoard" aria-label="Lucky Drop board">
          <canvas ref={canvasRef} width={BOARD_WIDTH} height={BOARD_HEIGHT} style={{ background: '#ffffff' }} />
          <span
            aria-hidden="true"
            className={`physicsCharm physicsCharm--${dropVisual}`}
            ref={charmVisualRef}
          >
            <img
              alt=""
              className={`physicsCharmImage physicsCharmImage--charm ${dropVisual === 'charm' ? 'physicsCharmImage--active' : ''}`}
              src={charmImage}
            />
            <img
              alt=""
              className={`physicsCharmImage physicsCharmImage--powerball ${dropVisual === 'powerball' ? 'physicsCharmImage--active' : ''}`}
              src={powerballBallImage}
            />
          </span>
          <div className="physicsSlots" aria-hidden="true">
            {slotValues.map((value, index) => (
              <span className={`physicsSlot ${landedSlot === index ? 'physicsSlot--winner' : ''}`} key={index}>
                {value}
              </span>
            ))}
          </div>
        </section>

        <button
          className="luckyDropButton"
          disabled={dropping}
          onClick={handleDrop}
          type="button"
        >
          Drop
        </button>
        <p className="luckyDropStatus">{status}</p>
      </div>
    </main>
  );
}

export default LuckyDrop;

export function readDropGame(routeGame, dropEntryVersion) {
  if (dropEntryVersion === 2 && (routeGame === 'mega' || routeGame === 'powerball')) {
    return routeGame;
  }
  return 'powerball';
}
